/**
 * WebcamCapture Component
 * Capture photos or videos from webcam with modern design
 */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { JobType } from "../../types";
import { useCameraAccess } from "../../hooks/useCameraAccess";
import { useFrameCapture } from "../../hooks/useFrameCapture";
import { useAppDispatch, jobsActions, uiActions } from "../../redux";
import { Button } from "../common";
import {
  Camera,
  Video,
  Square,
  RefreshCw,
  Rocket,
  RotateCcw,
  AlertCircle,
} from "lucide-react";

interface WebcamCaptureProps {
  jobType: JobType;
}

export const WebcamCapture: React.FC<WebcamCaptureProps> = ({ jobType }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { videoRef, isActive, error, startCamera, stopCamera, switchCamera } =
    useCameraAccess();
  const { extractFrame, startRecording, stopRecording } = useFrameCapture();

  const [capturedMedia, setCapturedMedia] = useState<Blob | null>(null);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  const recordingPromiseRef = useRef<Promise<Blob | null> | null>(null);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recording timer
  useEffect(() => {
    let interval: any;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const handleCapturePhoto = async () => {
    if (!videoRef.current || isCapturing) return;

    setIsCapturing(true);
    try {
      const frame = await extractFrame(videoRef.current);
      if (frame) {
        if (preview) {
          URL.revokeObjectURL(preview);
        }

        setCapturedMedia(frame);
        setMediaType("image");
        setPreview(URL.createObjectURL(frame));
      }
    } catch (error) {
      console.error("Failed to capture photo:", error);
      dispatch(
        uiActions.addNotification({
          type: "error",
          message: "Failed to capture photo",
        })
      );
    } finally {
      setIsCapturing(false);
    }
  };

  const handleStartRecording = async () => {
    if (!videoRef.current || isRecording) return;

    setIsRecording(true);
    setRecordingTime(0);

    if (preview) {
      URL.revokeObjectURL(preview);
      setCapturedMedia(null);
      setMediaType(null);
      setPreview(null);
    }

    try {
      recordingPromiseRef.current = startRecording(videoRef.current, 90000);
      const video = await recordingPromiseRef.current;

      if (video) {
        setCapturedMedia(video);
        setMediaType("video");
        setPreview(URL.createObjectURL(video));
      }
    } catch (error) {
      console.error("Recording failed:", error);
      dispatch(
        uiActions.addNotification({
          type: "error",
          message: "Recording failed",
        })
      );
    } finally {
      setIsRecording(false);
      recordingPromiseRef.current = null;
    }
  };

  const handleStopRecording = () => {
    if (!isRecording) return;
    stopRecording();
  };

  const handleRetake = () => {
    if (isRecording) {
      stopRecording();
      setIsRecording(false);
    }

    if (preview) {
      URL.revokeObjectURL(preview);
    }

    setCapturedMedia(null);
    setMediaType(null);
    setPreview(null);
    setRecordingTime(0);
  };

  const handleSubmit = async () => {
    if (!capturedMedia || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const file = new File(
        [capturedMedia],
        mediaType === "image" ? "capture.jpg" : "capture.webm",
        { type: capturedMedia.type }
      );

      const job = await dispatch(
        jobsActions.createJob({
          jobType,
          file: file,
          onProgress: (progress) => {
            dispatch(uiActions.setUploadProgress(progress));
          },
        })
      ).unwrap();

      dispatch(
        uiActions.addNotification({
          type: "success",
          message: "Capture submitted successfully!",
        })
      );

      navigate(`/jobs/${job.id}`);
    } catch (error: any) {
      dispatch(
        uiActions.addNotification({
          type: "error",
          message: error.message || "Failed to submit capture",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Glass Card Container */}
      <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 shadow-2xl">
        {error && (
          <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {!capturedMedia ? (
          <>
            {/* Camera Preview */}
            <div className="relative bg-slate-900 rounded-xl overflow-hidden mb-6 border border-slate-700/50">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-auto"
                style={{ maxHeight: "500px" }}
              />

              {isRecording && (
                <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-lg">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  <span className="font-medium">Recording: {recordingTime}s / 90s</span>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex gap-3">
              {isRecording ? (
                /* RECORDING MODE */
                <>
                  <Button
                    variant="ghost"
                    onClick={handleRetake}
                    disabled={true}
                    fullWidth
                    icon={<RotateCcw className="w-4 h-4" />}
                  >
                    Retake
                  </Button>
                  <button
                    onClick={handleStopRecording}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-all"
                  >
                    <Square className="w-4 h-4" />
                    Stop Recording
                  </button>
                </>
              ) : (
                /* IDLE MODE */
                <>
                  <button
                    onClick={handleCapturePhoto}
                    disabled={!isActive || isCapturing}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 disabled:from-slate-600 disabled:to-slate-700 text-white font-medium rounded-xl transition-all shadow-lg shadow-primary-500/20 disabled:shadow-none"
                  >
                    <Camera className="w-5 h-5" />
                    {isCapturing ? "Capturing..." : "Capture Photo"}
                  </button>

                  <button
                    onClick={handleStartRecording}
                    disabled={!isActive}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-white disabled:text-slate-500 font-medium rounded-xl border border-slate-600/50 transition-all"
                  >
                    <Video className="w-5 h-5" />
                    Record Video
                  </button>

                  <button
                    onClick={switchCamera}
                    disabled={!isActive}
                    className="p-3 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-slate-300 disabled:text-slate-500 rounded-xl border border-slate-600/50 transition-all"
                    title="Switch Camera"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            {/* PREVIEW MODE */}
            <div className="bg-slate-900 rounded-xl overflow-hidden mb-6 border border-slate-700/50">
              {mediaType === "image" ? (
                <img src={preview!} alt="Captured" className="w-full h-auto" />
              ) : (
                <video src={preview!} controls className="w-full h-auto" />
              )}
            </div>

            {/* PREVIEW MODE CONTROLS */}
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={handleRetake}
                disabled={isSubmitting}
                fullWidth
                icon={<RotateCcw className="w-4 h-4" />}
              >
                Retake
              </Button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 disabled:from-slate-600 disabled:to-slate-700 text-white font-medium rounded-xl transition-all shadow-lg shadow-primary-500/20"
              >
                {isSubmitting ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Rocket className="w-5 h-5" />
                )}
                {isSubmitting ? "Submitting..." : "Submit for Analysis"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
